import { centralAccessToken, rejectCentralSession } from './centralAuth'
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { apiBaseUrl } from '@/config/api'
import { readSession } from '@/helpers/storage'
import type { ApiResponse } from '@/models/api'
import type { Session } from '@/models/auth'
import { ApiError } from '@/services/apiError'



const listeners = new Set<(session: Session | null) => void>()


export const api = axios.create({ baseURL: apiBaseUrl })

export function subscribeSession(listener: (session: Session | null) => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function publishSession(session: Session | null) {
  listeners.forEach((listener) => listener(session))
}

export function readData<T>(data: ApiResponse<T> | undefined, fallback: T): T {
  if (!data?.success) {
    throw new ApiError(data?.messages)
  }
  return data.returnData ?? fallback
}

export function readError(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message
  }
  if (axios.isAxiosError(error)) {
    const messages = error.response?.data?.messages
    if (Array.isArray(messages) && messages.length > 0) {
      return messages.filter((message) => typeof message === 'string').join(' ')
    }
    const body = error.response?.data as { message?: string; detail?: string; title?: string; errors?: Record<string, string[]> } | undefined
    const detail = body?.message || body?.detail || body?.title
    if (typeof detail === 'string') return detail
    const validation = body?.errors
    if (validation && typeof validation === 'object') return Object.values(validation).flat().join(' ')
    if (error.response?.status === 401) {
      return 'Your session has ended. Sign in again.'
    }
    if (error.response?.status) {
      return `Request failed (${error.response.status})`
    }
  }
  if (error instanceof Error && error.message) {
    return error.message
  }
  return 'Request failed'
}

api.interceptors.request.use(async (config) => {
  const session = readSession()
  const token = session ? await centralAccessToken() : undefined
  if (session?.businessId) config.headers['X-Crm-Account-Id'] = session.businessId
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

api.interceptors.response.use(undefined, async (error: AxiosError) => {
  if (error.response?.status === 402) window.dispatchEvent(new Event('crm-access-changed'))
  if (error.response?.status === 401 && error.config) {
    const token = sentAccessToken(error.config)
    if (token) await rejectCentralSession(token)
  }
  return Promise.reject(error)
})

function sentAccessToken(config: InternalAxiosRequestConfig): string | null {
  const value = config.headers?.Authorization ?? config.headers?.authorization
  return typeof value === 'string' ? value.replace(/^Bearer\s+/i, '') : null
}
